/**
 * How a refund's status reads to an admin, and the tone it is shown in.
 *
 * The statuses are the server's (refund-summary-service.js in 2w-connect), most urgent first:
 * a refund 2W meant to write and could not is the one that needs a person, and a refund in
 * Shopify needs nobody. Red is reserved for the two a person has to act on.
 *
 * `reason` is the server's reason code for the two that carry one a person reads here:
 * NOT_WRITTEN (why 2W leaves it alone) and REFUSED (why the writer stopped). FAILED carries the
 * raw error instead, which is shown as it is.
 */

export const REFUND_STATUS_DISPLAY = Object.freeze({
  REFUSED: { label: 'Refused', tone: 'critical' },
  FAILED: { label: 'Write failed', tone: 'critical' },
  WAITING: { label: 'Waiting to write', tone: 'attention' },
  WRITING: { label: 'Writing', tone: 'info' },
  NOT_SETTLED: { label: 'Not settled by retailer', tone: undefined },
  WRITTEN: { label: 'Written to Shopify', tone: 'success' },
  NOT_WRITTEN: { label: 'Not written by 2W', tone: undefined },
});

const NOT_WRITTEN_REASONS = Object.freeze({
  WRITING_OFF: 'Writing refunds is off for this retailer',
  BEFORE_HANDOVER: 'Settled before 2W took over this retailer\'s refunds',
});

/** Label and tone for a status; an unknown status is shown as itself, untoned. */
export function refundStatusDisplay(status) {
  return REFUND_STATUS_DISPLAY[status] || { label: status || '—', tone: undefined };
}

/** The sentence under a status, or null when there is nothing more to say. */
export function refundStatusReason(status, reason) {
  if (!reason) return null;
  if (status === 'NOT_WRITTEN') return NOT_WRITTEN_REASONS[reason] || reason;
  if (status === 'NOT_SETTLED') return `Retailer state: ${reason}`;
  return reason;
}

/**
 * The per-order refund button an order's refunds call for, or null when there is nothing to
 * write: a refund in Shopify, one the retailer has not settled, and one 2W leaves alone on
 * purpose all have nothing a button could do. A refused or failed one is offered again,
 * because both can clear - the Shopify order appearing, a read that failed coming back.
 */
export function refundActionFor(refunds) {
  if (!refunds || !refunds.count) return null;
  if (refunds.status === 'WAITING') return { label: 'Write refund' };
  if (refunds.status === 'FAILED' || refunds.status === 'REFUSED') return { label: 'Retry refund' };
  return null;
}

export const WRITE_REFUND_TIP = 'Writes this order\'s retailer refunds into the brand\'s Shopify '
  + 'order now, as the brand\'s Write refunds would. Writes to Shopify, not to the retailer, and a '
  + 'Shopify refund cannot be undone.';

export const WRITE_REFUND_CONFIRM = 'Write this order\'s refund into the brand\'s Shopify store?\n\n'
  + 'A Shopify refund cannot be undone.';

export default {
  REFUND_STATUS_DISPLAY, refundStatusDisplay, refundStatusReason, refundActionFor,
};
